import { createFileRoute } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { NewShootDialog } from "@/components/forms/new-shoot-dialog";
import { shoots } from "@/mocks/data";

export const Route = createFileRoute("/bookings")({
  head: () => ({
    meta: [
      { title: "Bookings — Studio OS" },
      { name: "description", content: "Upcoming and active shoot bookings." },
    ],
  }),
  component: BookingsPage,
});

function BookingsPage() {
  const grouped = shoots.reduce<Record<string, typeof shoots>>((acc, s) => {
    (acc[s.date] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title="Bookings"
        description="All confirmed shoots, organized by date."
        actions={<NewShootDialog label="Book shoot" />}
      />
      <div className="space-y-8">
        {Object.entries(grouped).map(([date, items]) => (
          <section key={date}>
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {date}
              </h2>
              <span className="text-[11px] text-muted-foreground">
                {items.length} {items.length === 1 ? "shoot" : "shoots"}
              </span>
            </div>
            <div className="space-y-2">
              {items.map((s) => (
                <div key={s.id} className="flex items-center gap-6 rounded-xl bg-card p-4 ring-1 ring-border">
                  <div className="flex w-20 flex-none flex-col items-center border-r border-border pr-4">
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">Call</span>
                    <span className="font-mono text-lg font-medium">{s.callTime}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{s.duration}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium">{s.title}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="size-3" />
                      {s.location}
                    </p>
                    {s.notes ? <p className="mt-1 text-xs text-muted-foreground/80">{s.notes}</p> : null}
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
